import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api, {
  clearToken,
  getToken,
  refreshSession,
  setSessionLostHandler,
  setToken,
} from "@/lib/api";

const AuthContext = createContext(null);

/**
 * Session state.
 *
 * `user` is `null` while unknown, `false` when signed out, and the user object
 * when signed in. `offline` is the third case that used to be missing: the
 * server could not be reached, which is not the same as being signed out. The
 * old code cleared the token on any failed `/auth/me`, so a single 502 from
 * the database — which the client raises readily — signed the user out for
 * good and looked like being logged out by reloading the page.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [offline, setOffline] = useState(false);
  const started = useRef(false);

  const signOutLocally = useCallback(() => {
    clearToken();
    setUser(false);
    setOffline(false);
  }, []);

  const restore = useCallback(async () => {
    setOffline(false);
    try {
      // With no access token there may still be a valid refresh cookie —
      // after clearing site data, or once the token has been evicted. Ask
      // before concluding the user is signed out.
      if (!getToken()) await refreshSession();

      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        clearToken();
        setUser(false);
      } else {
        // Reachability problem. Keep the token; the session is probably fine.
        setOffline(true);
        setUser(false);
      }
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    if (started.current) return; // StrictMode mounts twice in development.
    started.current = true;
    setSessionLostHandler(signOutLocally);
    restore();
  }, [restore, signOutLocally]);

  const applyAuth = ({ access_token, user: account }) => {
    setToken(access_token);
    setOffline(false);
    setUser(account);
  };

  const logout = async () => {
    // Drops the refresh cookie server-side. Without it the cookie outlives the
    // sign-out and the next visit silently logs you back in.
    try {
      await api.post("/auth/logout");
    } catch {
      // Signing out must succeed locally even if the request does not.
    }
    signOutLocally();
  };

  return (
    <AuthContext.Provider
      value={{ user, ready, offline, retry: restore, applyAuth, logout, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
